import PropTypes from "prop-types";
import React from "react";
import { Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { ConnectedWsDeviceStatusConnStatus } from "ui/ws-ds-conn-status/connected-ws-ds-conn-status";
import { getErrorMessage } from "util/response";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

import { UpgradeConfig, OpType } from "./device-config";
import { DeviceSelector } from "./device-selector-v2";
import { VersionSelector } from "./version-selector";

class DeviceSelectList extends React.PureComponent {
  static get propTypes() {
    return {
      upgradeDeviceFilter: PropTypes.func,
      downgradeDeviceFilter: PropTypes.func,
      cancelUpgradeFilter: PropTypes.func,
      devices: PropTypes.object.isRequired,
      deviceStatusOnline: PropTypes.bool,
      onSubmit: PropTypes.func.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      availableVersions: PropTypes.func,
      successMessage: PropTypes.string,
      title: PropTypes.string.isRequired,
      versionSelectorEnabled: PropTypes.bool,
      startPage: PropTypes.number,
    };
  }

  static get defaultProps() {
    return {
      upgradeDeviceFilter: () => true,
      downgradeDeviceFilter: () => true,
      cancelUpgradeFilter: () => true,
      successMessage: "Success!",
      versionSelectorEnabled: false,
      availableVersions: () => [],
      startPage: 0,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      errorMessage: "",
      resetForm: false,
      submitted: false,
      successMessage: "",
      loading: true,
      isUpgrade: true,
      isAgent: false,
      isWebos: false,
      isCancelUpgrade: false,
      selectedOperation: props.startPage,
      selectedVersion: "",
      available_versions: props.availableVersions(),
      filteredDevices: {},
    };

    this.submit = this._submit.bind(this);
    this.handleResetComplete = this.handleResetComplete.bind(this);
    this.handleSelectType = this.handleSelectType.bind(this);
    this.handleSelectVersion = this.handleSelectVersion.bind(this);
    this.updateDeviceVersions = this.updateDeviceVersions.bind(this);
    this.applyDeviceFilter = this.applyDeviceFilter.bind(this);
    this.clearStatusMessages = this.clearStatusMessages.bind(this);

    this.latestVersion = "";
  }

  clearStatusMessages() {
    return { errorMessage: "", successMessage: "" };
  }

  _submit(selected) {
    if (!selected || selected.length === 0) {
      this.setState({ errorMessage: "Please select at least one device." });

      return;
    }

    const ids = selected.map((s) => s.id);

    this.setState(
      {
        errorMessage: "",
        submitted: true,
        successMessage: "",
      },
      this._postSubmit.bind(this, ids),
    );
  }

  componentDidMount() {
    const { availableVersions, startPage } = this.props;

    this.setState({
      loading: false,
      available_versions: availableVersions(),
    });

    this.handleSelectType(startPage);
  }

  _postSubmit(ids) {
    const { onSubmit } = this.props;
    const { selectedOperation, selectedVersion, filteredDevices } = this.state;
    const action = UpgradeConfig[selectedOperation].actionType;
    const targetVersion = selectedVersion
      ? selectedVersion
      : this.latestVersion;
    const successMessage = UpgradeConfig[selectedOperation].successMessage;

    return onSubmit(ids, action, targetVersion)
      .then(() => {
        const idsAsStrings = ids.map(String);
        const updatedFilteredDevices = Object.keys(filteredDevices)
          .filter((id) => !idsAsStrings.includes(id))
          .reduce((acc, id) => {
            acc[id] = filteredDevices[id];
            return acc;
          }, {});
        this.setState({
          successMessage,
          errorMessage: "",
          resetForm: true,
          submitted: false,
          filteredDevices: updatedFilteredDevices,
        });
      })
      .catch((e) => {
        this.setState({
          errorMessage: getErrorMessage(e),
          submitted: false,
          successMessage: "",
        });
      });
  }

  renderLoaderOrView() {
    const { storesLoaded } = this.props;
    const { loading } = this.state;

    if (!storesLoaded || loading) {
      return <NavLoader.Proxy />;
    }

    return (
      <Box title="" noHeader={true}>
        {this.renderView()}
      </Box>
    );
  }

  handleResetComplete() {
    this.setState({ resetForm: false });
  }

  handleSelectType(selectedOperation) {
    const isUpgrade = UpgradeConfig[selectedOperation].isUpgrade;
    const isAgent =
      UpgradeConfig[selectedOperation].id === OpType.UPGRADE_AGENT;
    const isWebos =
      UpgradeConfig[selectedOperation].id === OpType.UPGRADE_WEBOS;
    const isFirmware =
      UpgradeConfig[selectedOperation].id === OpType.UPGRADE_FIRMWARE;
    const isCancelUpgrade =
      UpgradeConfig[selectedOperation].id === OpType.CANCEL_UPGRADE;

    let filteredDevices = {};
    if (isCancelUpgrade) {
      filteredDevices = this.filterDevicesForCancel();
    } else if (isAgent || isWebos || isFirmware) {
      filteredDevices = this.filterDevices(isUpgrade, isAgent, isWebos, "");
    }
    this.setState({
      ...this.clearStatusMessages(),
      selectedOperation,
      isUpgrade,
      isAgent,
      isWebos,
      isCancelUpgrade,
      filteredDevices,
      selectedVersion: "",
    });
  }

  handleSelectVersion(selectedVersion) {
    const { isUpgrade, isAgent, isWebos } = this.state;
    const filteredDevices = this.filterDevices(
      isUpgrade,
      isAgent,
      isWebos,
      selectedVersion,
    );

    this.clearStatusMessages();
    this.setState({
      ...this.clearStatusMessages(),
      selectedVersion,
      filteredDevices,
    });
  }

  updateDeviceVersions(filteredDevices, isUpgrade, selectedVersion) {
    if (selectedVersion) {
      Object.values(filteredDevices).forEach((device) => {
        if (isUpgrade) {
          device.upgrade_version = selectedVersion;
        } else {
          device.downgrade_version = selectedVersion;
        }
      });
    }
  }

  applyDeviceFilter(
    device,
    isUpgrade,
    isAgent,
    isWebos,
    leastVersion,
    selectedVersion,
  ) {
    const { upgradeDeviceFilter, downgradeDeviceFilter } = this.props;

    const result = isUpgrade
      ? upgradeDeviceFilter(device, selectedVersion, isAgent, isWebos)
      : downgradeDeviceFilter(device, leastVersion, selectedVersion);
    if (!selectedVersion && (isAgent || isWebos) && result) {
      this.latestVersion = device.latest_version;
    }
    return result;
  }

  filterDevicesForCancel() {
    const { devices, cancelUpgradeFilter } = this.props;
    const filteredDevices = Object.values(devices)
      .filter((device) => cancelUpgradeFilter(device))
      .reduce((acc, device) => {
        acc[device.id] = device;
        return acc;
      }, {});

    return filteredDevices;
  }

  filterDevices(isUpgrade, isAgent, isWebos, selectedVersion) {
    const { devices } = this.props;
    const leastCurrentVersion = "3.0.0"; // TBD: Remove hardcoding
    const filteredDevices = Object.values(devices)
      .filter((device) =>
        this.applyDeviceFilter(
          device,
          isUpgrade,
          isAgent,
          isWebos,
          leastCurrentVersion,
          selectedVersion,
        ),
      )
      .reduce((acc, device) => {
        acc[device.id] = device;
        return acc;
      }, {});

    this.updateDeviceVersions(filteredDevices, isUpgrade, selectedVersion);
    return filteredDevices;
  }

  renderView() {
    const { deviceStatusOnline, versionSelectorEnabled, startPage } =
      this.props;

    const {
      errorMessage,
      resetForm,
      submitted,
      successMessage,
      available_versions,
      selectedOperation,
      selectedVersion,
      filteredDevices,
    } = this.state;

    return (
      <div>
        <Row>
          <Col xs={12}>
            <ConnectedWsDeviceStatusConnStatus />
          </Col>
        </Row>
        <VersionSelector
          versionSelectorEnabled={versionSelectorEnabled}
          available_versions={available_versions}
          onSelectType={this.handleSelectType}
          onSelectVersion={this.handleSelectVersion}
          startPage={startPage}
        />
        <Notification
          error={errorMessage}
          success={successMessage}
          successChildren={<Link to="/devices">Device List</Link>}
        />
        <DeviceSelector
          selectedOperation={selectedOperation}
          selectedVersion={selectedVersion}
          devices={filteredDevices}
          deviceStatusOnline={deviceStatusOnline}
          onResetComplete={this.handleResetComplete}
          resetForm={resetForm}
          submit={this.submit}
          submitted={submitted}
        />
      </div>
    );
  }

  render() {
    const { title } = this.props;
    return <Page title={title}>{this.renderLoaderOrView()}</Page>;
  }
}

export { DeviceSelectList };
